/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.18.0
 * @since 1.17.0
 */


import { EReportScheduleField, IReportScheduleData } from '@components/common/ReportSchedule/types';
import { TableNameType } from '@components/Table/types/tableTypes';
import { IAction, IActionError, IActionWithPayload } from '@modules/app/createAction';
import { ECalendarPeriod } from '@modules/internationalization/types/common/calendar';
import { EWizardAdditionalReports } from '@pages/WizardAdditionalReports/wizardAdditionalReportsConsant';

import {
    AUTOREPORT_ID_GET_META_FAILURE,
    AUTOREPORT_ID_GET_META_START,
    AUTOREPORT_ID_GET_META_SUCCESS,
    AUTOREPORT_ID_GET_TAB_META_FAILURE,
    AUTOREPORT_ID_GET_TAB_META_START,
    AUTOREPORT_ID_GET_TAB_META_SUCCESS,
    AUTOREPORT_ID_LOAD_FORMAT_META,
    AUTOREPORT_ID_LOAD_SCHEDULE_META,
    AUTOREPORT_ID_SAVE_TABLE,
    AUTOREPORT_ID_UPDATE_FORMAT,
    AUTOREPORT_ID_UPDATE_SCHEDULE,
} from './constants';
import { IAutoReportIdReducer } from './reducer';

export enum EPageAutoReportIdInfo {
    Accounts = 'accounts',
    FormatDelivery = 'formatDelivery',
    ScheduleParameters = 'scheduleParameters',
    Subscribers = 'subscribers',
}

export enum EPageAutoReportIdType {
    Account = 'A',
    Subscriber = 'S',
}

/*
* -------------------------------------------
*          AUTOREPORT ID META
* -------------------------------------------
*/

export type TAutoReportIdPayload = Pick<IAutoReportIdReducer, 'id'>;

export type TAutoReportIdGetMetaSuccessPayload = Pick<IAutoReportIdReducer, 'name' | 'type'>;

export type TAutoReportIdGetMetaStartAction =
    IActionWithPayload<typeof AUTOREPORT_ID_GET_META_START, TAutoReportIdPayload>;

export type TAutoReportIdGetMetaSuccessAction =
    IActionWithPayload<typeof AUTOREPORT_ID_GET_META_SUCCESS, TAutoReportIdGetMetaSuccessPayload>;

export type TAutoReportIdGetMetaFailureAction =
    IActionError<typeof AUTOREPORT_ID_GET_META_FAILURE>;


export interface IAutoReportIdGetTabMetaStartPayload {
    tabId: EPageAutoReportIdInfo;
}

export type TAutoReportIdGetTabMetaStartAction =
    IActionWithPayload<typeof AUTOREPORT_ID_GET_TAB_META_START, IAutoReportIdGetTabMetaStartPayload>;

export type TAutoReportIdGetTabMetaSuccessAction =
    IAction<typeof AUTOREPORT_ID_GET_TAB_META_SUCCESS>;

export type TAutoReportIdGetTabMetaFailureAction =
    IAction<typeof AUTOREPORT_ID_GET_TAB_META_FAILURE>;

/*
* -------------------------------------------
*          AUTOREPORT ID FORMAT
* -------------------------------------------
*/


export enum EAutoReportIdFormatField {
    ReportFormat = 'reportFormat',
    NotifyByPhoneEnabled = 'notifyByPhoneEnabled',
    NotifyByPhone = 'notifyByPhone',
    NotifyByEmailEnabled = 'notifyByEmailEnabled',
    NotifyByEmail = 'notifyByEmail',
    SendByEmailEnabled = 'sendByEmailEnabled',
    SendByEmail = 'sendByEmail',
    SendByEmailZip = 'sendByEmailZip',
    SendByEmailZipPasswordEnabled = 'sendByEmailZipPasswordEnabled',
    SendByEmailZipPassword = 'sendByEmailZipPassword',
}

export interface IAutoReportIdFormatFields {
    [EAutoReportIdFormatField.ReportFormat]: string;
    [EAutoReportIdFormatField.NotifyByPhoneEnabled]: boolean;
    [EAutoReportIdFormatField.NotifyByPhone]: string;
    [EAutoReportIdFormatField.NotifyByEmailEnabled]: boolean;
    [EAutoReportIdFormatField.NotifyByEmail]: string;
    [EAutoReportIdFormatField.SendByEmailEnabled]: boolean;
    [EAutoReportIdFormatField.SendByEmail]: string;
    [EAutoReportIdFormatField.SendByEmailZip]: boolean;
    [EAutoReportIdFormatField.SendByEmailZipPasswordEnabled]: boolean;
    [EAutoReportIdFormatField.SendByEmailZipPassword]: string;
}

export interface IAutoReportIdFormatMeta extends IAutoReportIdFormatFields {
    reportFormats: string[];
}

export type TAutoReportIdLoadFormatMetaAction =
    IActionWithPayload<typeof AUTOREPORT_ID_LOAD_FORMAT_META, IAutoReportIdFormatMeta>;

export type TAutoReportIdUpdateFormatAction =
    IActionWithPayload<typeof AUTOREPORT_ID_UPDATE_FORMAT, IAutoReportIdFormatFields>;


/*
* -------------------------------------------
*    AUTOREPORT ID SCHEDULE PARAMETERS
* -------------------------------------------
*/

export interface IAutoReportIdScheduleMeta {
    [EReportScheduleField.Description]?: string;
    [EReportScheduleField.Month]?: number;
    [EReportScheduleField.Period]?: {
        value: ECalendarPeriod;
    };
    [EReportScheduleField.ReportType]?: EWizardAdditionalReports;
    [EReportScheduleField.StartTime]?: string;
    [EReportScheduleField.TimeHour]?: {
        value: number;
    };
    [EReportScheduleField.TimeMinute]?: {
        value: number;
    };
    [EReportScheduleField.Week]?: {
        value: number;
    };
}

export type TAutoReportIdLoadScheduleMetaAction =
    IActionWithPayload<typeof AUTOREPORT_ID_LOAD_SCHEDULE_META, IAutoReportIdScheduleMeta>;

export type TAutoReportIdUpdateScheduleAction =
    IActionWithPayload<typeof AUTOREPORT_ID_UPDATE_SCHEDULE, IReportScheduleData>;

/*
* -------------------------------------------
*    AUTOREPORT ID TABLE
* -------------------------------------------
*/

export interface IAutoReportIdSaveTablePayload {
    tableName: TableNameType;
}

export type TAutoReportIdSaveTableAction =
    IActionWithPayload<typeof AUTOREPORT_ID_SAVE_TABLE, IAutoReportIdSaveTablePayload>;


export type TAutoReportIdActions =
    | TAutoReportIdGetMetaFailureAction
    | TAutoReportIdGetMetaStartAction
    | TAutoReportIdGetMetaSuccessAction
    | TAutoReportIdGetTabMetaFailureAction
    | TAutoReportIdGetTabMetaStartAction
    | TAutoReportIdGetTabMetaSuccessAction
    | TAutoReportIdLoadFormatMetaAction
    | TAutoReportIdLoadScheduleMetaAction
    | TAutoReportIdSaveTableAction
    | TAutoReportIdUpdateFormatAction
    | TAutoReportIdUpdateScheduleAction
